import { SORT_MODE_COUNTRY_POPULARITY, SortMode } from '../atoms/encodedFormAtoms';
import { ProxyCountryRanking } from '../types/proxyApi';

const DEFAULT_COUNTRIES: readonly string[] = ['United States'];

/**
 * URL-state country selection vs. the value the UI should display.
 *
 * `formParams.countries === undefined` means the user has never touched the
 * country picker — fall back to the default selection. An explicit empty
 * array means the user cleared everything and is respected as-is.
 */
export const computeEffectiveCountriesSelection = (countries: string[] | undefined): string[] =>
  countries ?? [...DEFAULT_COUNTRIES];

/**
 * Effective country list sent to the channels API. Country selection only
 * drives the request when the active sort is Country Popularity; under
 * Average Daily Views sort the country picker is hidden and we send no
 * countries even if the user previously had some selected.
 */
export const computeEffectiveCountries = (
  sortMode: SortMode,
  effectiveSelection: string[],
): string[] => (sortMode === SORT_MODE_COUNTRY_POPULARITY ? effectiveSelection : []);

/**
 * "Country Popularity" requires at least one country to be meaningful (the
 * rankings JOIN has nothing to bind to otherwise). When invalid, callers
 * should hold off the channels query and surface a validation error.
 */
export const computeIsCountrySelectionInvalid = (
  sortMode: SortMode,
  effectiveSelection: string[],
): boolean => sortMode === SORT_MODE_COUNTRY_POPULARITY && effectiveSelection.length === 0;

/**
 * True if the row has at least one ranking entry that matches the active
 * country selection. Empty rankings → false. No selection → any non-empty
 * rankings array passes.
 */
export const rowHasChipsForSelected = (
  rankings: ProxyCountryRanking[] | undefined,
  selectedCountries: string[] | undefined,
): boolean => {
  const list = rankings || [];
  if (list.length === 0) return false;
  const selected = new Set(selectedCountries || []);
  if (selected.size === 0) return true;
  return list.some((r) => selected.has(r.country_name));
};

/**
 * Whether the channels query (inclusion or exclusion) should fire. The
 * caller passes its own list-type expectation; the query is gated on:
 *   - the user being on the final step,
 *   - the active list-type matching what this caller serves,
 *   - the country selection being valid (relevant only under
 *     country-popularity sort).
 */
export const computeChannelsQueryEnabled = (
  finalStep: boolean,
  currentListType: string | null | undefined,
  expectedListType: 'inclusion' | 'exclusion',
  isCountrySelectionInvalid: boolean,
): boolean =>
  finalStep && currentListType === expectedListType && !isCountrySelectionInvalid;

/**
 * Compute where the "no impressions for selected countries" divider belongs
 * in the channel page. Returns the row index it should be inserted at, or
 * `null` if it shouldn't be drawn (no transition on this page, or the
 * partition is interleaved — i.e. a chips row appears after a no-chips
 * row, in which case any divider position would lie about the rows below).
 */
export const computeDividerInsertionIndex = <
  T extends { country_rankings?: ProxyCountryRanking[] },
>(
  rows: readonly T[],
  selectedCountries: string[] | undefined,
): number | null => {
  const firstNoChipsIdx = rows.findIndex(
    (row) => !rowHasChipsForSelected(row.country_rankings, selectedCountries),
  );
  if (firstNoChipsIdx === -1) return null;
  const tailAllNoChips = rows
    .slice(firstNoChipsIdx + 1)
    .every((row) => !rowHasChipsForSelected(row.country_rankings, selectedCountries));
  if (!tailAllNoChips) return null;
  return firstNoChipsIdx;
};
